import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ReportEventDragIndicator } from './ReportEventDragIndicator';

export interface ReportEventCardOptions {
    dragIndicator: ReactNode;
    isDragging: boolean;
}

interface SortableReportEventCardProps {
    id: string;
    reorderLabel: string;
    reorderable: boolean;
    isReorderPending: boolean;
    children: (options: ReportEventCardOptions) => ReactNode;
}

export function SortableReportEventCard({
    id,
    reorderLabel,
    reorderable,
    isReorderPending,
    children,
}: SortableReportEventCardProps) {
    const isDragDisabled = !reorderable || isReorderPending;
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled: isDragDisabled });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="group/report-event relative"
            // The whole card is the activator, so it carries the reorder label
            // that the indicator only mirrors as a hover tooltip.
            aria-label={reorderable ? reorderLabel : undefined}
            {...attributes}
            {...listeners}
        >
            {isDragging && (
                <div
                    aria-hidden
                    className="absolute inset-0 rounded-[10px] bg-grey-600 opacity-50"
                />
            )}

            <div className={cn(isDragging && 'invisible')}>
                {children({
                    dragIndicator: reorderable ? (
                        <ReportEventDragIndicator
                            label={reorderLabel}
                            testId={`report-event-drag-indicator-${id}`}
                        />
                    ) : null,
                    isDragging,
                })}
            </div>
        </div>
    );
}
